import React, {useEffect, useRef, useState} from 'react';
import {Alert, Box, CircularProgress, Stack, Typography} from '@mui/material';

/**
 * Plays a WebRTC stream from an SRS WHEP endpoint inside a `<video>` tag.
 *
 * The URL is used VERBATIM — it must be the same WHEP endpoint shape SRS
 * exposes (e.g. {@code http://srs.host:1985/rtc/v1/whep/?app=live&stream=cam1}),
 * which is what the {@code machine_camera} DB stores.
 *
 * Implementation follows the SRS reference SDK ({@code trunk/research/players/js/srs.sdk.js}):
 * createOffer → POST SDP to WHEP URL → setRemoteDescription with returned answer.
 * No external library dependency — vanilla `RTCPeerConnection` + `fetch`.
 *
 * Cleanup: closes the peer-connection on unmount and on URL change. Skipping
 * this leaks the SRS session until SRS's idle timeout (~30s) reaps it.
 */
export default function WhepVideoPlayer({url, label, aspectRatio = '16 / 9'}) {
    const videoRef = useRef(null);
    const pcRef = useRef(null);
    const [status, setStatus] = useState('idle'); // idle | connecting | playing | error
    const [error, setError] = useState(null);
    // Container aspect ratio: starts at `aspectRatio` prop (default 16:9),
    // overridden to the video's NATURAL aspect once metadata loads. This is
    // what kills the ugly pillarboxing — a portrait camera in a wide grid
    // column was rendering as a narrow strip with huge black bars around it.
    // With a matched aspect-ratio, the container shrinks to fit the video
    // exactly and `object-fit: contain` becomes a no-op.
    const [renderAspect, setRenderAspect] = useState(aspectRatio);

    useEffect(() => {
        if (!url || !url.trim()) {
            setStatus('idle');
            setError(null);
            return;
        }

        let cancelled = false;
        const pc = new RTCPeerConnection();
        pcRef.current = pc;
        setStatus('connecting');
        setError(null);

        pc.addTransceiver('audio', {direction: 'recvonly'});
        pc.addTransceiver('video', {direction: 'recvonly'});

        const mediaStream = new MediaStream();
        pc.ontrack = (event) => {
            mediaStream.addTrack(event.track);
            if (videoRef.current) {
                videoRef.current.srcObject = mediaStream;
            }
        };
        pc.onconnectionstatechange = () => {
            if (cancelled) return;
            const s = pc.connectionState;
            if (s === 'connected') setStatus('playing');
            if (s === 'failed' || s === 'disconnected' || s === 'closed') {
                setStatus('error');
                setError(`peer connection ${s}`);
            }
        };

        (async () => {
            try {
                const offer = await pc.createOffer();
                await pc.setLocalDescription(offer);
                const resp = await fetch(url, {
                    method: 'POST',
                    headers: {'Content-Type': 'application/sdp'},
                    body: offer.sdp,
                });
                if (!resp.ok) throw new Error(`WHEP ${resp.status} ${resp.statusText}`);
                const answerSdp = await resp.text();
                if (cancelled) return;
                await pc.setRemoteDescription({type: 'answer', sdp: answerSdp});
            } catch (e) {
                if (cancelled) return;
                setStatus('error');
                setError(e.message || String(e));
                pc.close();
            }
        })();

        return () => {
            cancelled = true;
            try { pc.close(); } catch (_) { /* ignore */ }
            if (videoRef.current) videoRef.current.srcObject = null;
            pcRef.current = null;
        };
    }, [url]);

    // Snap container to native video aspect once we know it. Centers the
    // player within the grid column (max-width keeps a very wide camera
    // from blowing past the column on a big screen).
    const onLoadedMetadata = () => {
        const v = videoRef.current;
        if (v && v.videoWidth > 0 && v.videoHeight > 0) {
            setRenderAspect(`${v.videoWidth} / ${v.videoHeight}`);
        }
    };

    return (
        <Box sx={{
            position: 'relative',
            width: '100%',
            aspectRatio: renderAspect,
            maxHeight: 360,             // hard ceiling so a tall portrait video doesn't dominate
            mx: 'auto',                 // center within the grid column
            bgcolor: 'grey.900',
            borderRadius: 1,
            overflow: 'hidden',
        }}>
            {label && (
                <Typography variant="caption"
                            sx={{position: 'absolute', top: 4, left: 6, color: 'white', zIndex: 1,
                                 textShadow: '0 0 4px rgba(0,0,0,0.8)'}}>
                    {label}
                </Typography>
            )}
            <video ref={videoRef} autoPlay playsInline muted
                   onLoadedMetadata={onLoadedMetadata}
                   style={{width: '100%', height: '100%', objectFit: 'contain', display: 'block', background: '#000'}}/>
            {status === 'connecting' && (
                <Stack direction="row" spacing={1} alignItems="center"
                       sx={{position: 'absolute', bottom: 6, left: 6, color: 'white'}}>
                    <CircularProgress size={14} color="inherit"/>
                    <Typography variant="caption">connecting…</Typography>
                </Stack>
            )}
            {status === 'error' && (
                <Alert severity="error" sx={{position: 'absolute', bottom: 0, left: 0, right: 0, fontSize: '0.75rem'}}>
                    {error}
                </Alert>
            )}
            {status === 'idle' && !url && (
                <Box sx={{position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
                          justifyContent: 'center', color: 'grey.500'}}>
                    <Typography variant="caption">no URL</Typography>
                </Box>
            )}
        </Box>
    );
}
